import React, { forwardRef, useImperativeHandle, useRef, useState } from "react";

import { productsApi } from "@redux/features/products/productsApi";
import BaseDropdown from "@reusables/dropdowns/BaseDropdown";
import BaseInfiniteLoader from "@reusables/dropdowns/BaseInfiniteLoader";

import { IntegrationGuard } from "@/components/reusables/BaseIntegrationGuard";
import BaseMaterialIconButton from "@/components/reusables/BaseMaterialIconButton";
import { Collapse, Fade, Typography } from "@mui/material";
import { isErrorWithMessage } from "@redux/api/query";
import { useAppDispatch } from "@redux/hooks";
import i18n from "i18next";
import { CornerDownRight, Pencil } from "lucide-react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { LineSchema, SOProduct } from "../../../../types";
import { ProductAlternativeNameEdit } from "./ProductAlternativeNameEdit";
import { AddProductFormSchemeTyping } from "@components/Dashboard/pages/Sales/Orders/components/MutationLayout/parts/ProductSection/modals/addProductDraftShared";

export interface ProductSelectionDropdownProps {
    selectedLine?: LineSchema;
}

export interface ProductSelectionDropdownHandle {
    focus: () => void;
}

const ProductSelectionDropdown = forwardRef<
    ProductSelectionDropdownHandle,
    ProductSelectionDropdownProps
>(function ProductSelectionDropdown({ selectedLine }, ref): JSX.Element {
    const { t } = useTranslation("", {
        keyPrefix: "sales.orders.general.orders.modals.addProduct",
    });

    const dispatch = useAppDispatch();
    const { control, setValue, setFocus } = useFormContext<AddProductFormSchemeTyping>();

    const [productsOptions, setProductsOptions] = useState<SOProduct[]>([]);
    const [isProductLazyLoadingFetching, setIsProductLazyLoadingFetching] = useState(false);
    const [isEditingAlternativeName, setIsEditingAlternativeName] = useState(false);
    const [searchValue, setSearchValue] = useState("");

    const inputRef = useRef<HTMLInputElement | null>(null);

    const clearLocalSelectionState = () => {
        // This dropdown owns only current-draft UI state: search text, lazy-loader results and
        // inline alternative-name editing. The surrounding draft-form remount handles cross-draft
        // cleanup, so this helper only responds to interactions inside the active draft session.
        setSearchValue("");
        setProductsOptions([]);
        setIsEditingAlternativeName(false);
    };

    // Expose focus only. Draft sessions are recreated by remounting the parent form, so the shell
    // no longer needs imperative cleanup hooks for this field.
    useImperativeHandle(ref, () => ({
        focus: () => {
            inputRef.current?.focus();
        },
    }));

    const focusQuantityField = () => {
        if (selectedLine) return;
        setTimeout(() => {
            setFocus("quantity");
        }, 100);
    };

    // Handle Enter key for barcode scanning
    const handleKeyDown = async (e: React.KeyboardEvent) => {
        if (selectedLine) return;
        if (e.key !== "Enter") return;

        const input = e.target as HTMLInputElement;
        const barcode = input.value.trim();
        if (!barcode) return;
        e.preventDefault();
        const result = await dispatch(
            productsApi.endpoints.getModuleProductsFull.initiate(
                {
                    filters: { search: barcode },
                    pagination: { page: 1, limit: 100 },
                    module: "sale",
                },
                { subscribe: false }
            )
        ).unwrap();

        const product = result?.payload?.find((p) => p.barcode === barcode);
        if (product) {
            setValue("product", product);
            clearLocalSelectionState();
            focusQuantityField();
        }
    };

    const alternativeName = useWatch({
        name: "alternative_name",
        control: control,
    });

    const selectedProduct = useWatch({
        control,
        name: "product",
    });

    return (
        <div>
            {isEditingAlternativeName ? (
                <ProductAlternativeNameEdit
                    // Passing initial value like this to ensure that RHF state is not editied
                    initialValue={alternativeName || selectedProduct?.name || ""}
                    onSave={(val) => {
                        if (val === selectedProduct?.name) {
                            setValue("alternative_name", null, {
                                shouldDirty: true,
                            });
                        } else {
                            setValue("alternative_name", val, {
                                shouldDirty: true,
                            });
                        }
                        setIsEditingAlternativeName(false);
                    }}
                    onCancel={() => setIsEditingAlternativeName(false)}
                />
            ) : (
                <Controller
                    name="product"
                    control={control}
                    render={({ field, fieldState }) => (
                        <div
                            onKeyDown={handleKeyDown}
                            ref={(el) => {
                                if (el) {
                                    const input = el.querySelector("input");
                                    inputRef.current = input;
                                }
                            }}
                        >
                            <BaseInfiniteLoader
                                fetch={(search, page, limit) => {
                                    setIsProductLazyLoadingFetching(true);
                                    dispatch(
                                        productsApi.endpoints.getModuleProductsFull.initiate(
                                            {
                                                filters: {
                                                    search,
                                                },
                                                pagination: {
                                                    page,
                                                    limit,
                                                },
                                                module: "sale",
                                            },
                                            {
                                                subscribe: false,
                                            }
                                        )
                                    )
                                        .unwrap()
                                        .then((res) => {
                                            setProductsOptions(res?.payload ?? []);
                                        })
                                        .catch((e) => {
                                            if (isErrorWithMessage(e)) {
                                                toast.error(e.message);
                                            } else {
                                                toast.error(
                                                    i18n.t("general.responses.somethingWentWrong")
                                                );
                                            }

                                            console.error(
                                                "An error occurred in the products lazy loader",
                                                e
                                            );
                                        })
                                        .finally(() => {
                                            setIsProductLazyLoadingFetching(false);
                                        });
                                }}
                                limit={100}
                                result={productsOptions}
                                isLoading={isProductLazyLoadingFetching}
                                skip={!!selectedLine}
                            >
                                {(infinity) => (
                                    <>
                                        <BaseDropdown
                                            {...field}
                                            {...fieldState}
                                            onChange={(
                                                newItem,
                                                _newState,
                                                _previousState,
                                                _event
                                            ) => {
                                                field.onChange(newItem);

                                                if (!newItem) {
                                                    clearLocalSelectionState();
                                                    return;
                                                }

                                                clearLocalSelectionState();
                                                if (newItem) {
                                                    focusQuantityField();
                                                }
                                            }}
                                            options={infinity.data}
                                            onOpen={() => {
                                                if (!infinity.data.length) {
                                                    infinity.trigger();
                                                }
                                            }}
                                            onInputChange={(_, value, reason) => {
                                                if (reason === "reset") return;

                                                // The visible text is derived from either the selected product
                                                // or the live search query. When the user starts typing again
                                                // after selecting a product, clear RHF's product value so the
                                                // text input and the stored field represent the same state.
                                                setSearchValue(value);
                                                infinity.onSearch(value);

                                                if (field.value) {
                                                    field.onChange(undefined);
                                                }
                                            }}
                                            inputValue={
                                                field.value ? field.value.name : searchValue
                                            }
                                            label={
                                                <div className="flex items-center justify-between">
                                                    {/*Product field label*/}
                                                    <span>{t("fields.product.label") + " *"}</span>

                                                    {/*Alternative Mame button*/}
                                                    <Fade in={!!field.value}>
                                                        <span>
                                                            <IntegrationGuard slug="twenty_four_seven_office">
                                                                <BaseMaterialIconButton
                                                                    tip={t(
                                                                        "fields.product.alternative_name.tooltip"
                                                                    )}
                                                                    tooltipPlacement="top"
                                                                    icon={<Pencil />}
                                                                    size="tiny"
                                                                    style="dim"
                                                                    onClick={() =>
                                                                        setIsEditingAlternativeName(
                                                                            true
                                                                        )
                                                                    }
                                                                />
                                                            </IntegrationGuard>
                                                        </span>
                                                    </Fade>
                                                </div>
                                            }
                                            placeholder={t("fields.product.placeholder")}
                                            getter={{
                                                label: (opt) => opt.name,
                                                key: (opt) => opt.id,
                                                caption: (opt) => opt.code,
                                            }}
                                            filterOptions={(option) =>
                                                option.name +
                                                "|" +
                                                option.code +
                                                "|" +
                                                option.barcode
                                            }
                                            lazyLoading={{
                                                loadNextPage: () => infinity.trigger(),
                                                hasNextPage: infinity.hasNextPage,
                                                isNextPageLoading:
                                                    infinity.isNextPageLoading || infinity.isTyping,
                                            }}
                                            isLoading={
                                                infinity.isNextPageLoading && !infinity.data.length
                                            }
                                            disabled={!!selectedLine}
                                            autofill={searchValue.trim().length > 0}
                                            virtualize
                                            autocomplete
                                        />
                                    </>
                                )}
                            </BaseInfiniteLoader>
                        </div>
                    )}
                />
            )}

            {/* Visual indicator of the previous value */}
            <Collapse in={!!alternativeName && !isEditingAlternativeName}>
                <div className="mt-2">
                    <Typography
                        variant="caption"
                        display="block"
                        sx={{ opacity: 0.5 }}
                        className="flex items-center space-x-2"
                    >
                        <CornerDownRight size={12} />
                        <span>{alternativeName}</span>
                    </Typography>
                </div>
            </Collapse>
        </div>
    );
});

export default ProductSelectionDropdown;
